import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login, me } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { AuthLanguagePicker } from "@/components/AuthLanguagePicker";

const DEMO_PASSWORD = "demo";
// One demo account per language: the seeded content — session titles, the coach
// narrative, the chat — lives in the rows, and those tables have no translation
// columns. `?demo=de` therefore has to pick a different account, not merely a
// different UI language. Unknown values fall back to the English one.
const DEMO_LANGS = ["ru", "uk", "es", "de", "fr", "it", "pt", "sv", "da"];
function demoEmail(param: string | null): string {
  return param && DEMO_LANGS.includes(param)
    ? `demo-${param}@mavinty.com`
    : "demo@mavinty.com";
}

export default function LoginPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const demoParam = params.get("demo");
  const isDemo = demoParam !== null;
  const DEMO_EMAIL = demoEmail(demoParam);
  // Prefilled from the query flag so the visitor lands on a filled form and can
  // press one button. Read from the initialiser rather than an effect, so the
  // inputs are never briefly empty.
  const [email, setEmail] = useState(isDemo ? DEMO_EMAIL : "");
  const [password, setPassword] = useState(isDemo ? DEMO_PASSWORD : "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const nav = useNavigate();
  const resetSuccess = params.get("reset") === "success";
  const pwChanged = params.get("pw") === "changed";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = await login(email, password);
      setAuth(token, email);
      try {
        await me();
      } catch {
        // no-op
      }
      nav("/", { replace: true });
    } catch (err) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || t("auth.login_error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <AuthLanguagePicker />
      <div className="mb-8 text-center">
        <h1 className="font-serif text-[34px] font-medium tracking-tight">{t("app.title")}</h1>
        <p className="mt-2 font-serif text-[15px] italic text-taupe">
          {t("app.tagline")}
        </p>
      </div>

      {resetSuccess || pwChanged ? (
        <div className="mb-4 rounded-lg border border-amber/40 bg-amber/10 px-4 py-3 text-[13px] text-navy-deep">
          {t("auth.password_changed_hint")}
        </div>
      ) : null}

      <Card className="space-y-4">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            type="email"
            label={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <Input
            type="password"
            label={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            minLength={8}
          />
          {error ? (
            <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {error}
            </div>
          ) : null}
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            {loading ? t("auth.signing_in") : t("auth.sign_in")}
          </Button>
        </form>

        {!isDemo && (
          <button
            type="button"
            onClick={() => {
              setEmail(DEMO_EMAIL);
              setPassword(DEMO_PASSWORD);
            }}
            className="mb-4 w-full rounded border border-dashed border-cream-border py-2 text-[13px] text-taupe hover:border-navy hover:text-navy focus:outline-none focus:ring-2 focus:ring-amber/45"
          >
            {t("auth.try_demo", "Look around a demo account first →")}
          </button>
        )}
        <div className="flex items-center justify-between border-t border-cream-border pt-4 text-[13px]">
          <Link
            to="/forgot-password"
            className="font-medium text-amber-dark hover:underline"
          >
            {t("auth.forgot_password")}
          </Link>
          <Link to="/register" className="text-taupe hover:text-navy">
            {t("auth.register")}
          </Link>
        </div>
      </Card>
    </div>
  );
}
